import { useState } from 'react';

interface EventItem {
  id: number;
  title: string;
  titleEn: string;
  icon: string;
  summary: string;
  details: {
    description: string;
    examples: string[];
    conditions: string[];
  };
}

const events: EventItem[] = [
  {
    id: 1,
    title: 'ライブイベント',
    titleEn: 'Live Music',
    icon: 'ri-music-2-line',
    summary: '音楽が空間を満たす夜',
    details: {
      description: 'ステージピアノ・マイク・デジタルミキサー・スピーカーを完備。アコースティックから小編成バンドまで対応できます。',
      examples: ['アコースティックライブ', 'ジャズセッション', 'シンガーソングライターのワンマン', '小編成バンドライブ'],
      conditions: ['定員：着席最大67名 / 立席最大100名', '設備：Roland RD-2000EX、マイク、デジタルミキサー、スピーカー', 'ご相談：Instagram DMにて承っています'],
    },
  },
  {
    id: 2,
    title: '展示 / アート展示',
    titleEn: 'Exhibition',
    icon: 'ri-gallery-line',
    summary: '日常の中にアートを',
    details: {
      description: 'カフェ営業と並行した展示も可能。壁面や空間を活かした個展・グループ展に対応しています。',
      examples: ['個展', 'グループ展', 'イラスト展', '写真展', 'インスタレーション'],
      conditions: ['期間：数日〜数週間の展示に対応', '形式：カフェ営業中の展示、または貸切展示', 'ご相談：Instagram DMにて承っています'],
    },
  },
  {
    id: 3,
    title: 'ポップアップショップ',
    titleEn: 'Pop-up Shop',
    icon: 'ri-store-2-line',
    summary: '一日限りの特別な出会い',
    details: {
      description: 'ブランドや作家の期間限定ショップとして空間を活用できます。カフェとの同時営業も可能です。',
      examples: ['ハンドメイド作品の販売', 'アパレルポップアップ', 'フード・スイーツの販売', 'クラフト雑貨'],
      conditions: ['形式：カフェ営業中の併設、または貸切', '設備：テーブル・什器の配置変更可能', 'ご相談：Instagram DMにて承っています'],
    },
  },
  {
    id: 4,
    title: 'ワークショップ',
    titleEn: 'Workshop',
    icon: 'ri-tools-line',
    summary: '学びと体験の場として',
    details: {
      description: 'ハーフスペースを使った少人数のワークショップに最適。テーブルを囲んで、手を動かす体験の場として。',
      examples: ['料理・フードワークショップ', 'クラフト・ハンドメイド', '写真・アートワークショップ', 'ヨガ・ボディワーク'],
      conditions: ['定員：16〜22名程度（ハーフスペース利用）', 'レイアウト：用途に合わせて変更可能', 'ご相談：Instagram DMにて承っています'],
    },
  },
  {
    id: 5,
    title: 'トークイベント',
    titleEn: 'Talk Event',
    icon: 'ri-chat-3-line',
    summary: '言葉が交わされる場所',
    details: {
      description: 'プロジェクターを使ったトークセッションや対談イベントに対応。少人数から中規模まで柔軟に対応できます。',
      examples: ['作家・クリエイターのトーク', '対談・インタビュー形式', 'パネルディスカッション', '読書会・勉強会'],
      conditions: ['設備：プロジェクター、マイク完備', '定員：規模に応じてレイアウト変更可能', 'ご相談：Instagram DMにて承っています'],
    },
  },
  {
    id: 6,
    title: '上映イベント',
    titleEn: 'Screening',
    icon: 'ri-film-line',
    summary: '映像と音が溶け合う夜',
    details: {
      description: 'プロジェクターとスピーカーを活用した映像上映イベントに対応。短編映画・ドキュメンタリー・映像作品の発表の場として。',
      examples: ['短編映画上映会', 'ドキュメンタリー上映', '映像作品の発表会', 'MV・映像作品の試写会'],
      conditions: ['設備：プロジェクター、スピーカー完備', '定員：着席最大67名', 'ご相談：Instagram DMにて承っています'],
    },
  },
];

export default function EventAccordion() {
  const [openId, setOpenId] = useState<number | null>(null);

  const toggle = (id: number) => {
    setOpenId(prev => (prev === id ? null : id));
  };

  return (
    <div className="divide-y divide-gray-200 border-t border-b border-gray-200 rounded-2xl overflow-hidden">
      {events.map(event => {
        const isOpen = openId === event.id;
        return (
          <div key={event.id}>
            {/* Header row */}
            <button
              onClick={() => toggle(event.id)}
              className="w-full flex items-center justify-between py-6 px-2 text-left cursor-pointer group"
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#E6E7E8] group-hover:bg-[#A790C4] transition-all duration-300">
                  <i className={`${event.icon} text-lg text-[#000000] group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <div>
                  <p className="text-base text-[#000000] font-light" style={{ fontFamily: 'serif' }}>{event.title}</p>
                  <p className="text-xs text-gray-400 tracking-widest mt-0.5" style={{ letterSpacing: '0.15em' }}>{event.titleEn}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 hidden sm:block" style={{ fontFamily: 'serif' }}>{event.summary}</span>
                <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
                  <i className="ri-add-line text-[#A790C4] text-lg"></i>
                </div>
              </div>
            </button>

            {/* Expandable content */}
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
            >
              <div className="pb-8 px-2 pl-14">
                <p className="text-sm text-gray-700 leading-relaxed mb-6" style={{ fontFamily: 'serif', lineHeight: '1.9' }}>
                  {event.details.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* 利用イメージ */}
                  <div>
                    <p className="text-xs tracking-widest text-[#000000] mb-3 font-light" style={{ letterSpacing: '0.2em' }}>利用イメージ</p>
                    <ul className="space-y-2">
                      {event.details.examples.map((ex, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-700" style={{ fontFamily: 'serif' }}>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#A790C4] flex-shrink-0 inline-block"></span>
                          {ex}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* 条件・設備 */}
                  <div>
                    <p className="text-xs tracking-widest text-[#000000] mb-3 font-light" style={{ letterSpacing: '0.2em' }}>条件・設備</p>
                    <ul className="space-y-2">
                      {event.details.conditions.map((cond, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700" style={{ fontFamily: 'serif' }}>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#386663] flex-shrink-0 inline-block mt-2"></span>
                          {cond}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-gray-100">
                  <a
                    href="https://instagram.com/episode_t21"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs text-[#A790C4] tracking-wider hover:text-[#386663] transition-colors cursor-pointer whitespace-nowrap"
                    style={{ letterSpacing: '0.15em' }}
                  >
                    <i className="ri-instagram-line text-base"></i>
                    Instagram DMで相談する
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}